import { useState } from 'react';
import {
  Loader2,
  AlertTriangle,
  RefreshCw,
  Home,
  User,
  DollarSign,
  MessageSquare,
  ChevronDown,
  Copy,
  Check,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useMeridianLinkLoan } from '../../hooks/useLosProxy';

interface Props {
  loanNumber: string;
}

function LoanField({ label, value }: { label: string; value: string | number | undefined | null }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div>
      <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-xs text-gray-800 dark:text-gray-200 mt-0.5">{String(value)}</p>
    </div>
  );
}

export default function MeridianLinkLoanViewer({ loanNumber }: Props) {
  const { data: loan, isLoading, error, refetch, isFetching } = useMeridianLinkLoan(loanNumber);
  const [showRawJson, setShowRawJson] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(loan, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!loanNumber) {
    return (
      <div className="text-center py-4">
        <Home className="h-6 w-6 text-gray-300 dark:text-gray-600 mx-auto mb-1" />
        <p className="text-sm text-gray-500">No loan number available for this order</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 justify-center text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading MeridianLink loan...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <AlertTriangle className="h-6 w-6 text-amber-400 mx-auto mb-1" />
        <p className="text-sm text-gray-500">Failed to load MeridianLink loan</p>
        <p className="text-xs text-gray-400 mt-1">{(error as Error).message}</p>
        <button onClick={() => refetch()} className="btn-secondary text-xs mt-3">Retry</button>
      </div>
    );
  }

  const applicant = loan?.applicants?.[0];
  const property = loan?.property;
  const loanInfo = loan?.loanInfo;
  const comments = loan?.comments || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-teal-400" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">MeridianLink Loan Data</h4>
        </div>
        <button onClick={() => refetch()} disabled={isFetching} className="btn-icon p-1.5" title="Refresh">
          <RefreshCw className={clsx('h-3.5 w-3.5', isFetching && 'animate-spin')} />
        </button>
      </div>

      {/* Loan Number */}
      <div className="p-2.5 rounded bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50">
        <LoanField label="Loan Number" value={loanNumber} />
      </div>

      {/* Applicant Info */}
      {applicant && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <User className="h-3 w-3 text-gray-400" />
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Primary Applicant</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pl-4">
            <LoanField label="First Name" value={applicant.firstName} />
            <LoanField label="Last Name" value={applicant.lastName} />
            <LoanField label="Email" value={applicant.email} />
            <LoanField label="Phone" value={applicant.phone} />
          </div>
        </div>
      )}

      {/* Property Info */}
      {property && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Home className="h-3 w-3 text-gray-400" />
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Property</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pl-4">
            <LoanField label="Address" value={property.address} />
            <LoanField label="City" value={property.city} />
            <LoanField label="State" value={property.state} />
            <LoanField label="ZIP" value={property.zip} />
            <LoanField label="County" value={property.county} />
            <LoanField label="Type" value={property.type} />
            <LoanField label="Value" value={property.value ? `$${property.value.toLocaleString()}` : undefined} />
          </div>
        </div>
      )}

      {/* Loan Info */}
      {loanInfo && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <DollarSign className="h-3 w-3 text-gray-400" />
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Loan Details</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pl-4">
            <LoanField label="Amount" value={loanInfo.loanAmount ? `$${loanInfo.loanAmount.toLocaleString()}` : undefined} />
            <LoanField label="Purpose" value={loanInfo.loanPurpose} />
            <LoanField label="Type" value={loanInfo.loanType} />
            <LoanField label="Interest Rate" value={loanInfo.interestRate ? `${loanInfo.interestRate}%` : undefined} />
            <LoanField label="Term" value={loanInfo.term ? `${loanInfo.term} months` : undefined} />
            <LoanField label="LTV" value={loanInfo.ltv ? `${loanInfo.ltv}%` : undefined} />
          </div>
        </div>
      )}

      {/* Comments */}
      {comments.length > 0 && (
        <div>
          <button
            onClick={() => setShowComments(!showComments)}
            className="flex items-center gap-1.5 mb-2"
          >
            <MessageSquare className="h-3 w-3 text-gray-400" />
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
              Comments ({comments.length})
            </p>
            <ChevronDown className={clsx('h-3 w-3 text-gray-400 transition-transform', showComments && 'rotate-180')} />
          </button>
          {showComments && (
            <div className="space-y-1.5 pl-4">
              {comments.map((comment, i) => (
                <div key={i} className="p-2.5 rounded bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50">
                  <div className="flex items-center gap-2 mb-1">
                    {comment.author && <span className="text-[10px] font-medium text-gray-600 dark:text-gray-400">{comment.author}</span>}
                    {comment.date && <span className="text-[10px] text-gray-400">{new Date(comment.date).toLocaleString()}</span>}
                  </div>
                  <p className="text-xs text-gray-700 dark:text-gray-300">{comment.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Raw JSON toggle */}
      <div>
        <button
          onClick={() => setShowRawJson(!showRawJson)}
          className="text-[11px] text-blue-500 hover:text-blue-400 flex items-center gap-1"
        >
          <ChevronDown className={clsx('h-3 w-3 transition-transform', showRawJson && 'rotate-180')} />
          {showRawJson ? 'Hide' : 'Show'} raw loan data
        </button>
        {showRawJson && (
          <div className="mt-2 relative">
            <button onClick={handleCopy} className="absolute top-2 right-2 btn-icon p-1" title="Copy JSON">
              {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />}
            </button>
            <pre className="text-[11px] text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50 rounded p-3 overflow-auto max-h-72 border border-gray-200 dark:border-gray-700/50 font-mono">
              {JSON.stringify(loan, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
